import { API_ENDPOINT, fetchWithAuth, parseApiResponse } from "./index";

const MAX_COVER_IMAGE_BYTES = 5 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
]);

export interface PresignedUploadResult {
  uploadUrl: string;
  imageUrl: string;
  s3Key: string;
}

export function validateCoverImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    return "Please choose a JPEG, PNG, WebP, GIF, or AVIF image.";
  }
  if (file.size > MAX_COVER_IMAGE_BYTES) {
    return "Image must be 5 MB or smaller.";
  }
  return null;
}

export async function requestEventImageUpload(options: {
  fileName: string;
  contentType: string;
  eventId?: number;
  eventSlug?: string;
}): Promise<PresignedUploadResult> {
  const body: Record<string, unknown> = {
    fileName: options.fileName,
    contentType: options.contentType,
  };
  if (options.eventId !== undefined) {
    body.eventId = options.eventId;
  }
  if (options.eventSlug) {
    body.eventSlug = options.eventSlug;
  }

  const response = await fetchWithAuth(`${API_ENDPOINT}/upload/event-image`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return parseApiResponse<PresignedUploadResult>(response);
}

export async function putFileToPresignedUrl(
  uploadUrl: string,
  file: File,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!response.ok) {
    throw new Error("Upload to storage failed");
  }
}

export async function uploadEventCoverImage(
  file: File,
  options: { eventId?: number; eventSlug?: string } = {},
): Promise<string> {
  const validationError = validateCoverImageFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const { uploadUrl, imageUrl } = await requestEventImageUpload({
    fileName: file.name,
    contentType: file.type,
    eventId: options.eventId,
    eventSlug: options.eventSlug,
  });
  await putFileToPresignedUrl(uploadUrl, file);
  return imageUrl;
}
